import type { QueryClient } from '@tanstack/react-query';
import type {
  GetNotificationsOutput,
  NotificationChangeEvent,
  NotificationCreatedEvent,
  NotificationDeletedEvent,
  NotificationOutput,
  NotificationReadEvent,
} from '@teo/backend';

/**
 * Updates notification caches based on subscription events
 * Intent: Single source of truth for cache updates, preventing race conditions
 */
export function updateNotificationCaches(queryClient: QueryClient, event: NotificationChangeEvent) {
  switch (event.type_) {
    case 'created':
      updateCacheForCreation(queryClient, event);
      break;
    case 'read':
      updateCacheForRead(queryClient, event);
      break;
    case 'deleted':
      updateCacheForDeletion(queryClient, event);
      break;
  }
}

/**
 * Handle notification creation cache updates
 */
function updateCacheForCreation(
  queryClient: QueryClient,
  notificationCreated: NotificationCreatedEvent,
) {
  queryClient.setQueriesData(
    { queryKey: [['notifications', 'getNotifications']] },
    (oldData: GetNotificationsOutput) => {
      if (!oldData) return oldData;

      const existingNotification = oldData.notifications.find(
        (notification: NotificationOutput) =>
          notification.notificationId === notificationCreated.notificationId,
      );

      if (existingNotification) {
        return oldData;
      }

      return {
        ...oldData,
        notifications: [notificationCreated, ...oldData.notifications].slice(0, 100),
        total: oldData.total + 1,
      };
    },
  );
}

/**
 * Handle notification read cache updates
 */
function updateCacheForRead(queryClient: QueryClient, readData: NotificationReadEvent) {
  queryClient.setQueriesData(
    { queryKey: [['notifications', 'getNotifications']] },
    (oldData: GetNotificationsOutput) => {
      if (!oldData || !readData.notificationIds) return oldData;

      const queryKey = queryClient
        .getQueryCache()
        .findAll({ queryKey: [['notifications', 'getNotifications']] })
        .find((query) => query.state.data === oldData)?.queryKey;

      // tRPC query keys are [path, { input, type }]; the options slot is
      // untyped in the cache, so narrow structurally before reading it.
      const keyOptions = queryKey?.[1];
      const input =
        keyOptions && typeof keyOptions === 'object' && 'input' in keyOptions
          ? (keyOptions as { input?: { statusFilter?: string } }).input
          : undefined;
      const isUnreadFilter = input?.statusFilter === 'unread';

      if (isUnreadFilter) {
        const filteredNotifications = oldData.notifications.filter(
          (notification: NotificationOutput) =>
            !readData.notificationIds.includes(notification.notificationId),
        );

        return {
          ...oldData,
          notifications: filteredNotifications,
          total: Math.max(
            0,
            oldData.total - (oldData.notifications.length - filteredNotifications.length),
          ),
        };
      } else {
        const updatedNotifications = oldData.notifications.map(
          (notification: NotificationOutput) => {
            if (readData.notificationIds.includes(notification.notificationId)) {
              return {
                ...notification,
                readAt: readData.readAt,
              };
            }
            return notification;
          },
        );

        return {
          ...oldData,
          notifications: updatedNotifications,
        };
      }
    },
  );
}

/**
 * Handle notification deletion cache updates
 */
function updateCacheForDeletion(queryClient: QueryClient, deletedData: NotificationDeletedEvent) {
  if (!deletedData.notificationIds) return;

  queryClient.setQueriesData(
    { queryKey: [['notifications', 'getNotifications']] },
    (oldData: GetNotificationsOutput) => {
      if (!oldData) return oldData;

      const notificationsToRemove = oldData.notifications.filter(
        (notification: NotificationOutput) =>
          deletedData.notificationIds.includes(notification.notificationId),
      );

      const filteredNotifications = oldData.notifications.filter(
        (notification: NotificationOutput) =>
          !deletedData.notificationIds.includes(notification.notificationId),
      );

      return {
        ...oldData,
        notifications: filteredNotifications,
        total: Math.max(0, oldData.total - notificationsToRemove.length),
      };
    },
  );
}
